// src/hooks/useUserDialogs.ts
import { useCallback } from 'react';

import type { UserRow } from '@/types/users.types';
import type { UiConfirmState } from '@/types/ui.types';
import { useModal } from '@/hooks/useModal';
import { useConfirm } from '@/hooks/useConfirm';

/**
 * User-specific convenience helpers for opening modals & confirms.
 */
export function useUserDialogs() {
  const { open: openModal, close: closeModal } = useModal();
  const { open: openConfirm } = useConfirm();

  const openCreateUser = useCallback(() => {
    openModal({ type: 'createUser' });
  }, [openModal]);

  const openEditUser = useCallback((user: UserRow) => {
    openModal({ type: 'editUser', user });
  }, [openModal]);

  const openUserCreated = useCallback((invitationLink: string, otp: string) => {
    openModal({ type: 'userCreated', invitationLink, otp });
  }, [openModal]);

  const confirmToggleActive = useCallback((row: UserRow) => {
    const payload: UiConfirmState = { context: 'users', kind: 'userToggleActive', row };
    openConfirm(payload);
  }, [openConfirm]);

  const confirmForceLogout = useCallback((row: UserRow) => {
    openConfirm({ context: 'users', kind: 'userForceLogout', row });
  }, [openConfirm]);

  const confirmForceLoginReset = useCallback((row: UserRow) => {
    openConfirm({ context: 'users', kind: 'userForceLoginReset', row });
  }, [openConfirm]);

  const confirmDelete = useCallback((row: UserRow) => {
    openConfirm({ context: 'users', kind: 'userDelete', row });
  }, [openConfirm]);

  return {
    openCreateUser,
    openEditUser,
    openUserCreated,
    confirmToggleActive,
    confirmForceLogout,
    confirmForceLoginReset,
    confirmDelete,
    closeModal,
  };
}
